/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import PageSkeleton from '../../components/common/page-skeleton';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { notificationMastersService } from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import { recipientMastersService } from '../../services/recipient-masters.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import ActiveTag from '../../components/common/active-tag';
import ChannelIcons from '../../components/common/channel-icons';
import TriggerAttachments from '../../components/notifications/trigger-attachments';
import { isRowActive } from '../../utils/active-state';
import {
  CARD, BackLink, CardHeader, Field, Hero, ActiveBanner, RecordMeta,
} from '../../components/notifications/engine-view-ui';

const JSON_PRE = 'max-h-72 overflow-auto rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-900/30 dark:text-white whitespace-pre-wrap break-words font-mono';

/**
 * The declared parameters with their samples, as the payload to fire.
 *
 * The box used to open empty, so firing anything meant writing the payload by hand or
 * pasting one — and a payload pasted one level too deep (`{"payload": {...}}`) resolves
 * nobody, which reads as a broken notification rather than a mistyped payload. Starting
 * from the notification's own samples makes the common case one click and the shape
 * obvious.
 */
const samplePayload = (master?: { parameters?: { name: string; sample?: unknown }[] }): string => {
  const parameters = master?.parameters || [];
  if (!parameters.length) return '{\n  \n}';
  return JSON.stringify(
    Object.fromEntries(parameters.map((p) => [p.name, p.sample ?? ''])), null, 2,
  );
};

const NotificationMasterView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'notification', basePath: '/notification-masters' });
  const canUpdate = useCanUpdate(JOB_NAMES.NOTIFICATION_MASTER);
  const toast = useRef<Toast>(null);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [payloadText, setPayloadText] = useState('{\n  \n}');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['notificationMaster', id],
    queryFn: () => notificationMastersService.getById(id!),
    enabled: isReady && !!id,
  });

  const { data: typesPage } = useQuery({
    queryKey: ['notificationTypes', 'lookup'],
    queryFn: () => notificationTypesService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  const { data: recipientsPage } = useQuery({
    queryKey: ['recipientMasters', 'lookup'],
    queryFn: () => recipientMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });

  const typeCode = (tid?: string) =>
    (typesPage?.data || []).find((t) => t._id === String(tid))?.notificationCode || String(tid || '-');
  // The notification delivers on its type's channels; it has no list of its own.
  const typeChannels = (tid?: string): string[] =>
    (typesPage?.data || []).find((t) => t._id === String(tid))?.channels || [];
  const recipientCode = (rid?: string) =>
    (recipientsPage?.data || []).find((r) => r._id === String(rid))?.recipientCode || String(rid || '-');

  const trigger = useMutation({
    mutationFn: () => {
      let payload: Record<string, unknown> = {};
      try { payload = JSON.parse(payloadText || '{}'); } catch { throw new Error('The payload is not valid JSON.'); }
      // A payload wrapped in another object — what pasting a documented example does —
      // puts every value one level too deep. The fire then resolves nobody and reports a
      // failed resolution, which reads as a broken notification rather than a wrong shape.
      if ('payload' in payload && Object.keys(payload).every((k) => k === 'payload' || k === 'context')) {
        throw new Error('This is the payload itself — unwrap it, so its fields sit at the top level.');
      }
      return notificationMastersService.trigger(id!, { payload });
    },
    onSuccess: () => {
      setTriggerOpen(false);
      toast.current?.show({ severity: 'success', summary: 'Fired', detail: 'See Notifications for what it did.', life: 5000 });
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Trigger failed', detail: extractErrorMessage(e, 'Trigger failed'), life: 10000 }),
  });

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Notification not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const recipients = data.recipients || [];
  const editable = canUpdate && !data.isDelete;

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-NotificationMaster-View">
      <Toast ref={toast} position="top-right" />
      <BackLink label="Notifications" onClick={() => navigateToList()} />

      <Hero
        icon="bell"
        chip="bg-indigo-50 text-indigo-500 dark:bg-indigo-950/50 dark:text-indigo-400"
        code={typeCode(data.notificationTypeId)}
        tags={<ActiveTag row={data} />}
        description={data.description}
        actions={<>
          {canUpdate && isRowActive(data) && <Button label="Trigger" icon="pi pi-play" size="small" severity="secondary" outlined onClick={() => { setPayloadText(samplePayload(data)); setTriggerOpen(true); }} />}
          {editable && <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" onClick={() => id && navigateToEdit(id)} />}
        </>}
        banner={isRowActive(data) && <ActiveBanner text="Active — edits apply from the next fire; notifications already sent are unchanged." />}
      />

      <div className={CARD}>
        <CardHeader icon="send" color="text-emerald-400" title="Channels" hint="A failure on one does not stop the rest" />
        <div className="px-4 py-4">
          <ChannelIcons channels={typeChannels(data.notificationTypeId)} variant="chips"
            empty="None — its type lists no channel, so nothing can be delivered." />
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="users" color="text-indigo-400" title={`Recipients (${recipients.length})`} hint="Each names an audience and its part" />
        <div className="px-4 py-4">
          {recipients.length ? (
            <ul className="flex flex-col gap-2">
              {recipients.map((r, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/30">
                  <span className="font-mono text-sm text-ink">{recipientCode(r.recipientMasterId)}</span>
                  {r.isMandatory && <Tag value="mandatory" severity="danger" className="text-xs px-1.5 py-0.5" />}
                  {r.selection?.mode === 'TOP_N' && (
                    <span className="text-xs text-ink-muted">
                      top {r.selection.topN ?? '?'}
                      {r.selection.rankingExpression ? ` by ${r.selection.rankingExpression}` : ''}
                    </span>
                  )}
                  {r.fallback?.enabled && (
                    <span className="text-xs text-ink-muted">fallback: {(r.fallback.chain || []).length} step(s)</span>
                  )}
                </li>
              ))}
            </ul>
          ) : <span className="text-sm text-gray-400">None — this notification resolves nobody.</span>}
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="code" color="text-sky-400" title="Configuration" />
        <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Parameters</div>
            <pre className={JSON_PRE}>{JSON.stringify(data.parameters || [], null, 2)}</pre>
          </div>
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Condition tree</div>
            <pre className={JSON_PRE}>{JSON.stringify(data.conditionTree || null, null, 2)}</pre>
          </div>
          <div className="sm:col-span-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Channel templates</div>
            <pre className={JSON_PRE}>{JSON.stringify(data.channelTemplates || {}, null, 2)}</pre>
          </div>
        </div>
      </div>

      <RecordMeta data={data} extra={<Field label="Notification type">{typeCode(data.notificationTypeId)}</Field>} />

      <Dialog header="Trigger this notification" visible={triggerOpen} onHide={() => setTriggerOpen(false)}
        style={{ width: '32rem' }} data-testid="NotificationMasterView.Trigger">
        <p className="text-sm text-ink-muted mb-2">
          Fires it now. This box is the payload itself, filled in from the declared parameters —
          edit the values, but keep the fields at the top level. The result appears under
          Notifications.
        </p>
        <InputTextarea value={payloadText} onChange={(e) => setPayloadText(e.target.value)} rows={8} className="w-full font-mono text-xs" />

        {/* A file for THIS fire, shared with the Engine Tester: the rules about where
            an upload goes, and what a per-recipient file is keyed on, belong in one
            place. Renders nothing when the templates name no file parameter. */}
        <TriggerAttachments
          master={data as any}
          payload={(() => {
            try { return JSON.parse(payloadText || '{}'); } catch { return {}; }
          })()}
          onChange={(next) => setPayloadText(JSON.stringify(next, null, 2))}
          ruleLabel={recipientCode}
          testId="NotificationMasterView.Trigger.Attach"
        />
        <div className="flex justify-end gap-2 mt-3">
          <Button label="Cancel" severity="secondary" outlined onClick={() => setTriggerOpen(false)} />
          <Button label={trigger.isPending ? 'Firing…' : 'Fire'} icon="pi pi-play" onClick={() => trigger.mutate()} disabled={trigger.isPending} />
        </div>
      </Dialog>
    </div>
  );
};

export default NotificationMasterView;
